<template>
  <div class="dashboard-container p-4">
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h1 class="text-2xl font-bold text-gray-800">
        Shooting Dashboard
      </h1>
      <p class="text-gray-600">
        Manage daily shooting schedule, equipment, and crew.
      </p>
    </div>
        
    <!-- Status Overview -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <Card class="shadow-sm">
        <template #header>
          <div class="flex justify-between items-center bg-blue-50 px-4 py-2">
            <h3 class="text-lg font-semibold text-blue-800">
              Production Status
            </h3>
            <i class="pi pi-video text-blue-600" />
          </div>
        </template>
        <template #content>
          <div class="p-3">
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Current Project</span>
              <span class="font-semibold">Symphony Documentary</span>
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Day</span>
              <span class="font-semibold">8 of 14</span>
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Location</span>
              <span class="font-semibold">Symphony Hall</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-700">Status</span>
              <Tag
                value="On Schedule"
                severity="secondary"
              />
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #header>
          <div class="flex justify-between items-center bg-orange-50 px-4 py-2">
            <h3 class="text-lg font-semibold text-orange-800">
              Today's Forecast
            </h3>
            <i class="pi pi-cloud text-orange-600" />
          </div>
        </template>
        <template #content>
          <div class="p-3">
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Weather</span>
              <span class="font-semibold">Partly Cloudy</span>
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Temperature</span>
              <span class="font-semibold">72°F / 22°C</span>
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Sunrise</span>
              <span class="font-semibold">06:24 AM</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-700">Sunset</span>
              <span class="font-semibold">08:17 PM</span>
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #header>
          <div class="flex justify-between items-center bg-green-50 px-4 py-2">
            <h3 class="text-lg font-semibold text-green-800">
              Equipment Status
            </h3>
            <i class="pi pi-camera text-green-600" />
          </div>
        </template>
        <template #content>
          <div class="p-3">
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Camera Packages</span>
              <Tag
                value="Ready"
                severity="secondary"
              />
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Lighting Gear</span>
              <Tag
                value="Ready"
                severity="secondary"
              />
            </div>
            <div class="flex justify-between items-center mb-3">
              <span class="text-gray-700">Sound Equipment</span>
              <Tag
                value="Ready"
                severity="secondary"
              />
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-700">Grip Equipment</span>
              <Tag
                value="In Transit"
                severity="warning"
              />
            </div>
          </div>
        </template>
      </Card>
    </div>
        
    <!-- Quick Actions Buttons -->
    <div class="flex flex-wrap gap-2 mb-4">
      <Button
        label="Call Sheet"
        icon="pi pi-file-pdf"
        severity="secondary"
      />
      <Button
        label="Shot List"
        icon="pi pi-list"
        severity="secondary"
      />
      <Button
        label="Equipment Checkout"
        icon="pi pi-briefcase"
        severity="secondary"
      />
      <Button
        label="Report Issue"
        icon="pi pi-exclamation-triangle"
        severity="secondary"
      />
    </div>
        
    <!-- Shooting Schedule -->
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h2 class="text-xl font-semibold mb-4">
        Today's Shooting Schedule
      </h2>
      <DataTable
        :value="shootingSchedule"
        striped-rows
        responsive-layout="scroll"
      >
        <Column
          field="time"
          header="Time"
          style="width: 15%"
        />
        <Column
          field="scene"
          header="Scene"
          style="width: 15%"
        />
        <Column
          field="description"
          header="Description"
          style="width: 35%"
        />
        <Column
          field="location"
          header="Location"
          style="width: 20%"
        />
        <Column
          field="status"
          header="Status"
          style="width: 15%"
        >
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status"
              :severity="getScheduleStatusSeverity(slotProps.data.status)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
        
    <!-- Crew Availability -->
    <div class="bg-white p-5 rounded-lg shadow">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">
          Crew Availability
        </h2>
        <Button
          label="Full Crew List"
          icon="pi pi-users"
          text
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          v-for="(crew, index) in crewAvailability"
          :key="index"
          class="shadow-sm"
        >
          <template #content>
            <div class="flex items-center p-3">
              <Avatar
                :icon="crew.avatar"
                :style="{ backgroundColor: crew.color, color: '#ffffff' }"
                class="mr-3"
                size="large"
                shape="circle"
              />
              <div>
                <h4 class="text-lg font-semibold mb-1">
                  {{ crew.department }}
                </h4>
                <p class="text-gray-600 mb-2">
                  {{ crew.members }} members on set
                </p>
                <Tag
                  :value="crew.status"
                  :severity="getCrewStatusSeverity(crew.status)"
                />
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// Sample shooting schedule
const shootingSchedule = ref([
    { 
        time: '07:30 - 08:30', 
        scene: 'Setup',
        description: 'Equipment setup and location prep',
        location: 'Symphony Hall - Main Stage',
        status: 'Completed'
    },
    { 
        time: '08:30 - 10:30', 
        scene: 'Scene 12',
        description: 'Orchestra rehearsal wide shots',
        location: 'Symphony Hall - Main Stage',
        status: 'Completed'
    },
    { 
        time: '10:45 - 12:30', 
        scene: 'Scene 14',
        description: 'Conductor interview',
        location: 'Symphony Hall - Green Room',
        status: 'In Progress'
    },
    { 
        time: '13:30 - 15:00', 
        scene: 'Scene 15',
        description: 'String section closeups during performance',
        location: 'Symphony Hall - Main Stage',
        status: 'Pending'
    },
    { 
        time: '15:15 - 17:00', 
        scene: 'Scene 17',
        description: 'Audience reaction shots during finale',
        location: 'Symphony Hall - Audience Seating',
        status: 'Pending'
    }
]);

// Sample crew availability
const crewAvailability = ref([
    {
        department: 'Camera',
        members: 5,
        status: 'All Present',
        avatar: 'pi pi-camera',
        color: '#3B82F6'
    },
    {
        department: 'Sound',
        members: 3,
        status: 'All Present',
        avatar: 'pi pi-volume-up',
        color: '#10B981'
    },
    {
        department: 'Lighting',
        members: 4,
        status: '1 Delayed',
        avatar: 'pi pi-sun',
        color: '#F59E0B'
    },
    {
        department: 'Production',
        members: 6,
        status: 'All Present',
        avatar: 'pi pi-cog',
        color: '#6366F1'
    },
    {
        department: 'Direction',
        members: 2,
        status: 'All Present',
        avatar: 'pi pi-directions',
        color: '#EC4899'
    },
    {
        department: 'Grip',
        members: 4,
        status: '1 Missing',
        avatar: 'pi pi-wrench',
        color: '#EF4444'
    }
]);

// Function to determine schedule status severity for tags
const getScheduleStatusSeverity = (status) => {
    switch (status.toLowerCase()) {
        case 'completed':
            return 'success';
        case 'in progress':
            return 'info';
        case 'pending':
            return 'secondary';
        case 'delayed':
            return 'warning';
        case 'cancelled':
            return 'danger';
        default:
            return 'secondary';
    }
};

// Function to determine crew status severity for tags
const getCrewStatusSeverity = (status) => {
    if (status.toLowerCase().includes('all present')) {
        return 'success';
    } else if (status.toLowerCase().includes('delayed')) {
        return 'warning';
    } else if (status.toLowerCase().includes('missing')) {
        return 'danger';
    } else {
        return 'info';
    }
};
</script>

<style scoped>
.dashboard-container {
    background-color: #f5f7fa;
    min-height: 100vh;
}
</style> 